import React from "react";
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from "react-hook-form";
import { FormField, FormItem, FormMessage, FormInput } from "./form";
import { UI_CONFIG } from "../../constants/autostack";
import {
  formatNumberWithCommas,
  parseFormattedNumber,
} from "../../utils/utils";

interface StackAmountInputProps {
  name: string;
  placeholder?: string;
  className?: string;
  prefix?: string;
  label?: string;
}

export const StackAmountInput: React.FC<StackAmountInputProps> = React.memo(
  ({
    name,
    placeholder = "5,000",
    className = "",
    prefix = "R",
    label = "Amount",
  }) => {
    const form = useFormContext();

    const processValue = (value: string): string => {
      const rawValue = parseFormattedNumber(value);
      return rawValue && Number(rawValue) > 0 ? rawValue : "";
    };

    const handleChange = (
      value: string,
      field: ControllerRenderProps<FieldValues, string>
    ) => {
      const processedValue = processValue(value);
      if (processedValue || value === "") {
        field.onChange(processedValue);
      }
    };

    const handleBlur = (
      value: string,
      field: ControllerRenderProps<FieldValues, string>
    ) => {
      const processedValue = processValue(value);
      field.onChange(processedValue);
      field.onBlur();
    };

    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem className={className}>
            <div className="flex flex-col gap-1 w-full md:w-auto">
              <div className="relative w-full">
                <span
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-base md:text-lg"
                  aria-hidden="true"
                >
                  {prefix}
                </span>
                <FormInput
                  type="text"
                  placeholder={placeholder}
                  className={`${UI_CONFIG.FORM_HEIGHT} pl-8 text-base`}
                  aria-label={`Enter ${label}`}
                  value={formatNumberWithCommas(field.value || "")}
                  onChange={(e) => handleChange(e.target.value, field)}
                  onBlur={(e) => handleBlur(e.target.value, field)}
                  name={field.name}
                />
              </div>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
    );
  }
);

StackAmountInput.displayName = "StackAmountInput";
